import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout, PageTransition, useModal } from '../../components/layout';
import { useProfileManagement } from '../../components/auth/useProfileManagement';
import { GoogleDrivePicker } from '../../components/data-sources/google-drive/GoogleDrivePicker';
import { SyncedDriveFiles } from '../../components/data-sources/google-drive/SyncedDriveFiles';
import { Alert } from 'react-bootstrap';
import { User } from '../../types/knowledge-base.ts';
import { ProfileData } from '../../types/auth';
import { GOOGLE_OAUTH_CLIENT_ID } from "../../config/api.config";

const DriveFilesPage: React.FC = () => {
    const { user, isLoading: authLoading } = useAuth();
    const modalState = useModal();
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [profileData, setProfileData] = useState<ProfileData>({
        fullName: user?.fullName || '',
        email: user?.email || ''
    });

    const profileManagement = useProfileManagement(
        undefined,
        modalState.setError,
        modalState.setSuccess,
        modalState.setIsLoading,
        modalState.setShowProfileModal,
        modalState.setShowPasswordModal
    );

    const handleUploadSuccess = () => {
        setSuccessMessage('Files synced successfully');
        setRefreshKey(prev => prev + 1);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    const handleError = (error: string) => {
        setErrorMessage(error);
        setTimeout(() => setErrorMessage(null), 3000);
    };

    const handleSuccess = (message: string) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    if (authLoading) {
        return (
            <PageTransition>
                <div className="container mt-5">
                    <div className="text-center">Loading...</div>
                </div>
            </PageTransition>
        );
    }

    if (!user) {
        return (
            <PageTransition>
                <div className="container mt-5">
                    <div className="alert alert-warning">
                        Please log in to access Google Drive integration.
                    </div>
                </div>
            </PageTransition>
        );
    }

    const layoutUser: User = {
        id: user.id.toString(),
        fullName: user.fullName || 'Unknown User',
        email: user.email || 'unknown@example.com',
        organizationId: user.organizationId,
        organizationDomain: user.organizationDomain || '',
        isSystemOrg: user.isSystemOrg,
        role: user.role === 'admin' ? 'admin' : 'user',
        status: user.status || 'active',
        groups: user.groups || []
    };

    const isAdmin = layoutUser.role === 'admin';

    return (
        <MainLayout
            user={layoutUser}
            modalProps={{
                profileData,
                error: modalState.error,
                success: modalState.success,
                isLoading: modalState.isLoading,
                showProfileModal: modalState.showProfileModal,
                handleProfileUpdate: (e) => profileManagement.handleProfileUpdate(e, profileData),
                handleDataChange: (data: Partial<ProfileData>) => {
                    if ('fullName' in data || 'email' in data) {
                        setProfileData(prev => ({ ...prev, ...data }));
                    }
                },
                setShowProfileModal: modalState.setShowProfileModal
            }}
        >
            <PageTransition>
                <div className="page-container">
                    <div className="mb-4">
                        <h5 className="mb-3">Google Drive Source</h5>
                        <div className="dashboard-card">
                            {errorMessage && (
                                <Alert variant="danger" className="mb-3" dismissible
                                       onClose={() => setErrorMessage(null)}>
                                    {errorMessage}
                                </Alert>
                            )}
                            {successMessage && (
                                <Alert variant="success" className="mb-3" dismissible
                                       onClose={() => setSuccessMessage(null)}>
                                    {successMessage}
                                </Alert>
                            )}

                            <div className="mb-4">
                                <GoogleDrivePicker
                                    onUploadSuccess={handleUploadSuccess}
                                    clientId={GOOGLE_OAUTH_CLIENT_ID}
                                    onError={handleError}
                                />
                            </div>

                            <SyncedDriveFiles
                                key={refreshKey}
                                onError={handleError}
                                onSuccess={handleSuccess}
                                isAdmin={isAdmin}
                            />
                        </div>
                    </div>
                </div>
            </PageTransition>
        </MainLayout>
    );
};

export default DriveFilesPage;